<template>
  <div class="pre-competency-tab">
    <v-card class="mb-6">
      <v-card-title class="primary--text">
        <v-icon left color="primary">mdi-eye-check</v-icon>
        Pre Competency Analysis
      </v-card-title>
      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-sheet rounded class="metric-sheet pa-4" :style="kpiStyleByPercent(globalMatch)">
              <div class="caption grey--text">Competency Match</div>
              <div class="text-h5 font-weight-bold">{{ formatPercent(globalMatch) }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="12" md="4">
            <v-sheet rounded class="metric-sheet pa-4" :style="kpiStyleBy010(globalScore010)">
              <div class="caption grey--text">Visible Behaviours Score (0-10)</div>
              <div class="text-h5 font-weight-bold">{{ formatDecimal(globalScore010) }}</div>
            </v-sheet>
          </v-col>
          <v-col cols="12" md="4">
            <v-sheet
              rounded
              class="metric-sheet pa-4"
              :style="kpiStyleByPercent(similarityPercentage)"
            >
              <div class="caption grey--text">Scenario Similarity</div>
              <div class="text-h5 font-weight-bold">{{ formatPercent(similarityPercentage) }}</div>
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card v-if="criticalScenarios.length" class="mb-6">
      <v-card-title class="text-subtitle-1">
        <v-icon left small>mdi-alert-decagram-outline</v-icon>
        Position Scenarios ({{ criticalScenarios.length }})
      </v-card-title>
      <v-card-text>
        <v-expansion-panels v-model="openScenarioPanels" multiple>
          <v-expansion-panel v-for="(item, idx) in criticalScenarios" :key="`sc-${idx}`">
            <v-expansion-panel-header>
              <div class="d-flex align-center w-100">
                <span class="font-weight-medium mr-3"
                  >Scenario {{ item.scenario_id || idx + 1 }}</span
                >
                <v-chip small :color="getScoreColor(item.similarity_percentage)" dark>
                  {{ formatPercent(item.similarity_percentage) }}
                </v-chip>
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="mb-2">
                <strong>Interviewer question:</strong>
                <div class="mt-1">{{ item.interviewer_question || 'Not detected.' }}</div>
              </div>
              <div class="mb-2">
                <strong>Expected answer:</strong>
                <div class="mt-1">{{ item.expected_answer || 'Not detected.' }}</div>
              </div>
              <div class="mb-2">
                <strong>Candidate answer (this scenario):</strong>
                <div class="mt-1">{{ item.applicant_answer || 'Not detected.' }}</div>
              </div>
              <div>
                <strong>Evaluation:</strong>
                <div class="mt-1">
                  {{ item?.similarity_analysis?.evaluation_explanation || 'Not detected.' }}
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <v-card v-if="unifiedBehaviourRows.length > 0" class="mb-6">
      <v-card-title class="text-subtitle-1">
        <v-icon left small>mdi-view-grid-outline</v-icon>
        Visible behaviours ({{ unifiedBehaviourRows.length }})
      </v-card-title>
      <v-card-text class="pt-2">
        <v-row dense class="vb-cards-row">
          <v-col
            v-for="behaviour in unifiedBehaviourRows"
            :key="behaviour.id"
            cols="12"
            md="6"
            class="d-flex"
          >
            <v-card class="vb-behaviour-card flex-grow-1 d-flex flex-column" outlined elevation="0">
              <v-card-title class="vb-card-title pb-2 align-start">
                <div class="flex-grow-1 pr-2" style="min-width: 0">
                  <div class="subtitle-1 font-weight-bold">{{ behaviour.name }}</div>
                  <div v-if="behaviour.description" class="caption grey--text mt-1">
                    {{ behaviour.description }}
                  </div>
                  <v-chip x-small outlined class="mt-2">
                    Weight: {{ behaviour.positionWeight }}
                  </v-chip>
                </div>
                <v-chip :color="getScoreColor(behaviour.score)" dark class="flex-shrink-0 mt-0">
                  {{ formatScore(behaviour.score) }}
                </v-chip>
              </v-card-title>
              <v-divider />
              <v-card-text class="flex-grow-1 d-flex flex-column pt-3 pb-3" style="min-height: 0">
                <div class="mb-4">
                  <div class="caption font-weight-bold text-uppercase grey--text mb-2">
                    Observable strengths
                  </div>
                  <ul class="vb-bullet-list">
                    <li v-for="(ev, idx) in behaviour.strengths" :key="`s-${behaviour.id}-${idx}`">
                      {{ ev }}
                    </li>
                    <li v-if="!behaviour.strengths.length" class="grey--text font-italic">
                      Not detected.
                    </li>
                  </ul>
                </div>
                <div class="mb-4">
                  <div class="caption font-weight-bold text-uppercase grey--text mb-2">
                    Development areas
                  </div>
                  <ul class="vb-bullet-list">
                    <li
                      v-for="(ev, idx) in behaviour.improvements"
                      :key="`i-${behaviour.id}-${idx}`"
                    >
                      {{ ev }}
                    </li>
                    <li v-if="!behaviour.improvements.length" class="grey--text font-italic">
                      None listed.
                    </li>
                  </ul>
                </div>
                <div class="vb-literal-block mt-auto">
                  <div class="caption font-weight-bold text-uppercase grey--text mb-2">
                    Literal evidence
                  </div>
                  <div v-if="behaviour.literalEvidence" class="literal-evidence-text body-2">
                    {{ behaviour.literalEvidence }}
                  </div>
                  <div v-else class="caption grey--text">
                    No literal evidence in analysis payload.
                  </div>
                  <div
                    v-if="
                      behaviour.evidenceSeconds != null &&
                      Number.isFinite(behaviour.evidenceSeconds)
                    "
                    class="mt-2"
                  >
                    <v-btn
                      text
                      small
                      color="primary"
                      class="px-0"
                      @click="onSeekEvidence(behaviour.evidenceSeconds)"
                    >
                      <v-icon left small>mdi-play-circle-outline</v-icon>
                      {{ formatClock(behaviour.evidenceSeconds) }}
                    </v-btn>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="mb-6">
      <v-card-title class="text-subtitle-1">
        <v-icon left small>mdi-code-json</v-icon>
        Full pre competency payload
      </v-card-title>
      <v-card-text>
        <div class="raw-json-container">
          <JsonTreeViewer
            v-if="preCompetencyResult && Object.keys(preCompetencyResult).length"
            :data="preCompetencyResult"
          />
          <p v-else class="grey--text mb-0">No pre competency analysis payload available.</p>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import JsonTreeViewer from './JsonTreeViewer.vue';

export default {
  name: 'PreCompetencyTab',
  components: { JsonTreeViewer },

  props: {
    // Store key (composite analysisKey or numeric id) used for cross-tab video seek signals.
    interviewId: {
      type: [Number, String],
      default: null,
    },
    analysisData: {
      type: Object,
      default: () => ({}),
    },
    beliefsAnalysis: {
      type: Object,
      default: () => null,
    },
  },
  data() {
    return {
      openScenarioPanels: [0],
    };
  },

  computed: {
    fullInterviewResult() {
      return this.beliefsAnalysis?.fullInterviewResult || null;
    },

    preCompetencyResult() {
      return this.fullInterviewResult?.results?.pre_competencial || null;
    },

    competencialAnalysis() {
      return (
        this.beliefsAnalysis?.beliefsAnalysis?.competencialAnalysis ||
        this.beliefsAnalysis?.competencialAnalysis ||
        null
      );
    },

    globalMatch() {
      return (
        this.preCompetencyResult?.global_match ??
        this.competencialAnalysis?.global_match?.score ??
        null
      );
    },

    similarityPercentage() {
      const pre = this.preCompetencyResult;
      const leg = this.competencialAnalysis;
      return (
        pre?.similarity_percentage ??
        pre?.similarityPercentage ??
        leg?.similarity_percentage ??
        leg?.similarityPercentage ??
        null
      );
    },
    scores() {
      return this.preCompetencyResult?.scores || {};
    },
    globalScores() {
      return this.scores?.global_scores || {};
    },
    globalScore010() {
      return this.globalScores?.score ?? null;
    },
    individualScores() {
      return Array.isArray(this.scores?.individual_scores) ? this.scores.individual_scores : [];
    },
    individualScoreById() {
      const map = {};
      this.individualScores.forEach((item) => {
        const key = String(item?.visible_behaviour_id || item?.cv_id || '');
        if (key) map[key] = item;
      });
      return map;
    },

    /**
     * Maps visible behaviour id -> literal string from multilayer_analysis (layer1_dictionary, etc.).
     * Matches backend scratch: scores.multilayer_analysis.layer1_dictionary.detected_cvs[].literal
     */
    multilayerLiteralByBehaviourId() {
      const map = {};
      const ml =
        this.preCompetencyResult?.multilayer_analysis ||
        this.preCompetencyResult?.scores?.multilayer_analysis ||
        this.competencialAnalysis?.multilayer_analysis ||
        this.competencialAnalysis?.scores?.multilayer_analysis ||
        null;
      if (!ml || typeof ml !== 'object') return map;
      const layerKeys = ['layer1_dictionary', 'layer2_thinkpills', 'layer3_padre'];
      layerKeys.forEach((layerKey) => {
        const layer = ml[layerKey];
        const detected = layer?.detected_cvs ?? layer?.detectedCvs;
        if (!Array.isArray(detected)) return;
        detected.forEach((row) => {
          const id = String(
            row?.visible_behaviour_id ?? row?.visibleBehaviourId ?? row?.cv_id ?? row?.cvId ?? ''
          );
          if (!id || map[id]) return;
          const lit = (row?.literal ?? '').toString().trim();
          if (lit) map[id] = lit;
        });
      });
      return map;
    },
    criticalScenarios() {
      const pre = this.preCompetencyResult;
      const legacy = this.competencialAnalysis;
      const raw =
        (Array.isArray(pre?.critical_scenarios) && pre.critical_scenarios) ||
        (Array.isArray(pre?.criticalScenarios) && pre.criticalScenarios) ||
        (Array.isArray(legacy?.critical_scenarios) && legacy.critical_scenarios) ||
        (Array.isArray(legacy?.criticalScenarios) && legacy.criticalScenarios) ||
        [];
      return raw.map((row, idx) => this.normalizeCriticalScenarioRow(row, idx));
    },
    extraById() {
      const raw = this.preCompetencyResult?.extra;
      return raw && typeof raw === 'object' ? raw : {};
    },
    infoById() {
      const map = {};
      const rows = this.preCompetencyResult?.information_per_behaviour;
      if (Array.isArray(rows)) {
        rows.forEach((item) => {
          const key = String(item?.visible_behaviour_id || item?.cv_id || '');
          if (key) map[key] = item;
        });
      }
      return map;
    },
    fallbackBehaviourRows() {
      const rows = [];
      const fromInfo = this.preCompetencyResult?.information_per_behaviour;
      if (Array.isArray(fromInfo) && fromInfo.length) {
        return fromInfo.map((item) => ({
          visible_behaviour_id: item?.visible_behaviour_id || item?.cv_id,
          competency_id: item?.visible_behaviour_id || item?.cv_id,
          competency_name: item?.cv_name || '',
          score: item?.score,
          strengths: item?.strengths || [],
          improvements: item?.improvements || [],
          position_required: item?.position_required,
          position_weight: item?.position_weight,
        }));
      }
      Object.entries(this.extraById).forEach(([id, item]) => {
        rows.push({
          visible_behaviour_id: id,
          competency_id: id,
          competency_name: item?.name || '',
          score: item?.score,
          strengths: item?.strengths || [],
          improvements: item?.improvements || [],
          position_required: item?.position_required,
          position_weight: item?.position_weight,
        });
      });
      return rows;
    },

    behavioursList() {
      const primary = Array.isArray(this.preCompetencyResult?.visible_behaviour)
        ? this.preCompetencyResult.visible_behaviour
        : [];
      const sourceRows = primary.length ? primary : this.fallbackBehaviourRows;
      return sourceRows.map((b) => {
        const key = String(
          b?.visible_behaviour_id ||
            b?.visibleBehaviourId ||
            b?.competency_id ||
            b?.competencyId ||
            b?.id ||
            ''
        );
        const fromIndividual = this.individualScoreById[key] || {};
        const fromInfo = this.infoById[key] || {};
        const fromExtra = this.extraById[key] || {};
        const strengthsArr = Array.isArray(b?.strengths)
          ? b.strengths
          : Array.isArray(fromInfo?.strengths)
            ? fromInfo.strengths
            : Array.isArray(fromExtra?.strengths)
              ? fromExtra.strengths
              : [];
        const multilayerLit = this.multilayerLiteralByBehaviourId[key] || '';
        // Prefer the backend's verified literal evidence (a verbatim substring of the
        // candidate answer); only fall back to the multilayer/strengths-derived text.
        const verifiedLiteral = String(b?.literal_evidence ?? b?.literalEvidence ?? '').trim();
        const literalEvidence =
          verifiedLiteral ||
          multilayerLit ||
          (strengthsArr[0] ? String(strengthsArr[0]).trim() : '');
        const resolvedName =
          b?.name ||
          b?.competency_name ||
          b?.competencyName ||
          b?.cv_name ||
          fromIndividual?.cv_name?.name ||
          fromInfo?.cv_name ||
          fromExtra?.name ||
          '';
        return {
          id: key,
          name: resolvedName || `Behaviour ${key || 'N/A'}`,
          description: b?.description || fromExtra?.description || '',
          score: b?.score ?? fromInfo?.score ?? fromExtra?.score ?? 0,
          strengths: strengthsArr,
          improvements: Array.isArray(b?.improvements)
            ? b.improvements
            : Array.isArray(fromInfo?.improvements)
              ? fromInfo.improvements
              : Array.isArray(fromExtra?.improvements)
                ? fromExtra.improvements
                : [],
          positionWeight:
            b?.position_weight ??
            b?.positionWeight ??
            fromIndividual?.position_weight ??
            fromInfo?.position_weight ??
            fromExtra?.position_weight ??
            0,
          literalEvidence,
          evidenceTimestampSeconds:
            b?.evidence_timestamp_seconds ?? b?.evidenceTimestampSeconds ?? null,
        };
      });
    },

    unifiedBehaviourRows() {
      const weightNum = (w) => {
        const n = Number(w);
        return Number.isFinite(n) ? n : 0;
      };
      const rows = this.behavioursList.map((b) => ({
        ...b,
        evidenceSeconds: this.resolveEvidenceSecondsForBehaviour(b),
      }));
      return [...rows].sort((a, b) => {
        const diff = weightNum(b.positionWeight) - weightNum(a.positionWeight);
        if (diff !== 0) return diff;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
    },
  },

  methods: {
    ...mapActions('analysisData', ['requestInterviewVideoSeek']),

    normalizeCriticalScenarioRow(item, idx) {
      const row = item && typeof item === 'object' ? item : {};
      const nested = row.similarity_analysis || row.similarityAnalysis || {};
      const evalExpl =
        nested.evaluation_explanation ??
        nested.evaluationExplanation ??
        row.evaluation_explanation ??
        row.evaluationExplanation ??
        '';
      const sim =
        row.similarity_percentage ??
        row.similarityPercentage ??
        nested.similarity_percentage ??
        nested.similarityPercentage ??
        null;
      return {
        scenario_id: row.scenario_id ?? row.scenarioId ?? idx + 1,
        similarity_percentage: sim,
        status: row.status ?? 'unknown',
        interviewer_question:
          row.interviewer_question ??
          row.interviewerQuestion ??
          row.question ??
          row.interviewer_question_text ??
          row.interviewerQuestionText ??
          '',
        expected_answer: row.expected_answer ?? row.expectedAnswer ?? row.expected ?? '',
        applicant_answer:
          row.applicant_answer ??
          row.applicantAnswer ??
          row.candidate_answer ??
          row.candidateAnswer ??
          '',
        similarity_analysis: {
          ...nested,
          evaluation_explanation: evalExpl,
        },
      };
    },

    resolveEvidenceSecondsForBehaviour(behaviour) {
      // Prefer the backend's verified evidence timestamp (no fuzzy matching needed).
      const direct = behaviour.evidenceTimestampSeconds;
      if (direct != null && Number.isFinite(Number(direct))) return Number(direct);
      const lit = (behaviour.literalEvidence || '').trim();
      if (lit.length >= 6) {
        const byLit = this.findTranscriptionTimeForSnippet(lit);
        if (byLit != null) return byLit;
      }
      const s = (behaviour.strengths && behaviour.strengths[0]) || '';
      const i = (behaviour.improvements && behaviour.improvements[0]) || '';
      return this.findTranscriptionTimeForSnippet(s) ?? this.findTranscriptionTimeForSnippet(i);
    },

    findTranscriptionTimeForSnippet(snippet) {
      const entries = this.analysisData?.transcription;
      if (!snippet || !Array.isArray(entries)) return null;
      const needle = String(snippet).trim().toLowerCase().slice(0, 80);
      if (needle.length < 6) return null;
      for (const e of entries) {
        const cand = String(e.candidateAnswer || e.answer || e.text || '').toLowerCase();
        if (cand.includes(needle)) {
          const st = e.startTime;
          if (st != null && Number.isFinite(Number(st))) return Number(st);
        }
      }
      return null;
    },

    onSeekEvidence(seconds) {
      if (this.interviewId == null || this.interviewId === '') return;
      this.requestInterviewVideoSeek({ analysisId: this.interviewId, seconds });
    },

    formatClock(seconds) {
      const s = Number(seconds);
      if (!Number.isFinite(s) || s < 0) return 'N/A';
      const m = Math.floor(s / 60);
      const sec = Math.floor(s % 60);
      return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
    },

    kpiToneColor(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '#b0bec5';
      const v = Number(value);
      if (v >= 80) return '#66bb6a';
      if (v >= 65) return '#42a5f5';
      if (v >= 45) return '#ffca28';
      return '#ef5350';
    },
    kpiStyleByPercent(percent) {
      const color = this.kpiToneColor(percent);
      return {
        backgroundColor: `${color}33`,
        border: `1px solid ${color}88`,
        boxShadow: `inset 0 0 0 1px ${color}33`,
      };
    },
    kpiStyleBy010(score010) {
      if (score010 === null || score010 === undefined || Number.isNaN(Number(score010))) {
        return this.kpiStyleByPercent(null);
      }
      return this.kpiStyleByPercent(Number(score010) * 10);
    },
    getScoreColor(score) {
      if (!score && score !== 0) return 'grey';
      if (score >= 80) return 'success';
      if (score >= 70) return 'warning';
      return 'error';
    },

    formatScore(score) {
      if (score === null || score === undefined || Number.isNaN(Number(score))) return '--';
      return `${Math.round(Number(score))}%`;
    },
    formatPercent(score) {
      if (score === null || score === undefined || Number.isNaN(Number(score))) return '--';
      return `${Math.round(Number(score))}%`;
    },
    formatDecimal(value) {
      if (value === null || value === undefined || Number.isNaN(Number(value))) return '--';
      return Number(value).toFixed(2);
    },
  },
};
</script>

<style scoped>
.pre-competency-tab {
  padding: 16px 0;
}

.metric-sheet {
  background: #f7f9fc;
  border: 1px solid #e5eaf2;
}

.raw-json-container {
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 16px;
  border-radius: 8px;
  overflow: auto;
  max-height: 70vh;
}

.vb-cards-row {
  margin-top: 0;
}

.pre-competency-tab .vb-behaviour-card {
  border-radius: 10px !important;
  height: 100%;
  box-shadow: none !important;
}

.vb-card-title {
  flex-wrap: nowrap;
  word-break: break-word;
}

.vb-bullet-list {
  margin: 0;
  padding-left: 1.15rem;
  line-height: 1.5;
}

.vb-bullet-list li + li {
  margin-top: 6px;
}

.vb-literal-block {
  padding-top: 0;
}

/* Vuetify body-2 can force Roboto; keep Comfortaa for literal block */
.vb-behaviour-card .literal-evidence-text,
.vb-behaviour-card .vb-literal-block .caption.grey--text {
  font-family: Comfortaa, 'Comfortaa', sans-serif !important;
}

.literal-evidence-text {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.45;
}

.vb-behaviour-card >>> .v-btn {
  font-family: Comfortaa, 'Comfortaa', sans-serif !important;
}
</style>
